import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth, hasRole, canRead } from "@/lib/auth";
import { api } from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";
import {
  House, ChartLineUp, FileArrowUp, Export, EnvelopeSimple,
  Users, ListMagnifyingGlass, SignOut, Sparkle, PencilSimpleLine,
  Target, ChatCircleDots, List as ListIcon, X, CaretDoubleLeft, CaretDoubleRight,
  Database, CalendarBlank,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: House, roles: [], module: "dashboard" },
  { to: "/monthly", label: "Monthly Sales", icon: CalendarBlank, roles: [], module: "monthly_sales" },
  // TEMP-HIDDEN: Reports menu — re-enable when the page is ready for users.
  // { to: "/reports", label: "Reports", icon: ChartLineUp, roles: [], module: "reports" },
  { to: "/targets", label: "Targets", icon: Target, roles: [], module: "targets" },
  { to: "/chat", label: "Analyst AI", icon: ChatCircleDots, roles: [], module: "chat" },
  { to: "/upload", label: "Upload Center", icon: FileArrowUp, roles: ["super_admin", "management"], module: "upload" },
  { to: "/data-fixer", label: "Data Fixer", icon: PencilSimpleLine, roles: ["super_admin", "management"], module: "data_fixer" },
  { to: "/export", label: "Export", icon: Export, roles: [], module: "export" },
  { to: "/email", label: "Email Reports", icon: EnvelopeSimple, roles: [], module: "email" },
  { to: "/admin", label: "Admin", icon: Users, roles: ["super_admin", "management"], module: "admin" },
  { to: "/audit", label: "Audit Logs", icon: ListMagnifyingGlass, roles: ["super_admin", "management"], module: "audit" },
];

// Persisted key for the desktop collapsed state.
const COLLAPSE_KEY = "myb.sidebar.collapsed";

const BrandBlock = ({ compact }) => (
  <div className={`relative flex items-center ${compact ? "justify-center px-2 py-4" : "px-6 py-5"}`}>
    <div className="w-11 h-11 rounded-sm bg-myb-500 flex items-center justify-center shadow-sm shrink-0">
      <img src="/myb-logo.png" alt="Morad Yousuf Behbehani" className="w-8 h-8 object-contain" style={{ filter: "brightness(0) invert(1)" }} />
    </div>
    {!compact && (
      <div className="ml-3 min-w-0">
        <div className="text-[10px] tracking-[0.3em] text-myb-500 uppercase leading-tight font-semibold">Morad Yousuf</div>
        <div className="font-display text-xl leading-tight text-foreground font-light truncate">Behbehani</div>
        <div className="text-[9px] text-muted-foreground mt-0.5 uppercase tracking-[0.25em]">Since 1935</div>
      </div>
    )}
  </div>
);

const NavItems = ({ items, compact, onNavigate }) => (
  <nav className={`flex-1 py-3 overflow-y-auto overflow-x-hidden scrollbar-thin space-y-0.5 ${compact ? "px-2" : "px-3"}`}>
    {items.map((item) => (
      <NavLink
        key={item.to}
        to={item.to}
        data-testid={`nav-${item.to.slice(1)}`}
        onClick={onNavigate}
        title={compact ? item.label : undefined}
        className={({ isActive }) =>
          `group flex items-center gap-3 rounded-sm transition-all duration-200 ${
            compact ? "justify-center px-0 py-2.5" : "px-3 py-2.5"
          } ${
            isActive
              ? "bg-myb-500 text-white shadow-sm"
              : "text-muted-foreground hover:text-myb-500 hover:bg-myb-50/70"
          }`
        }
      >
        <item.icon weight="duotone" size={18} className="shrink-0" />
        {!compact && <span className="text-[13px] font-medium tracking-wide truncate">{item.label}</span>}
      </NavLink>
    ))}
  </nav>
);

const UserFooter = ({ user, compact, onLogout }) => (
  <div className={`border-t hairline ${compact ? "p-2" : "p-4"}`}>
    {!compact && (
      <>
        <div className="text-[10px] text-muted-foreground uppercase tracking-[0.2em] font-semibold">Signed in</div>
        <div className="mt-1 text-sm font-medium truncate">{user?.name}</div>
        <div className="text-[10px] text-myb-500 uppercase tracking-wider font-semibold mt-0.5 truncate">{user?.role?.replace("_", " ")}</div>
      </>
    )}
    <button
      data-testid="logout-btn"
      onClick={onLogout}
      title="Sign out"
      className={`${compact ? "mt-0 w-10 h-10 mx-auto" : "mt-3 w-full py-2"} flex items-center justify-center gap-2 text-xs border border-myb-100 hover:border-myb-500 hover:text-myb-500 text-muted-foreground transition-all rounded-sm font-medium`}
    >
      <SignOut size={14} />
      {!compact && <span>Sign out</span>}
    </button>
  </div>
);

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Desktop collapsed — persists across reloads
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === "1");
  // Mobile drawer open/close
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Data freshness — shown in header so every page tells the user
  // which sales date all KPIs / presets are anchored on.
  const [dataInfo, setDataInfo] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/system/data-info").then((r) => setDataInfo(r.data)).catch(() => {});
  }, [user, location.pathname]);

  useEffect(() => {
    localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  // Close mobile drawer on route change.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Close drawer on Escape
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setDrawerOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  const items = NAV.filter((n) =>
    (n.roles.length === 0 || hasRole(user, ...n.roles)) &&
    (!n.module || canRead(user, n.module))
  );

  const handleLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen flex text-foreground bg-background">
      {/* ── Desktop sidebar (lg+) ───────────────────────────────────────── */}
      <aside
        data-testid="sidebar-desktop"
        data-collapsed={collapsed ? "1" : "0"}
        className={`hidden lg:flex shrink-0 flex-col bg-white border-r hairline relative z-20 no-print transition-[width] duration-200 ease-out ${
          collapsed ? "w-16" : "w-64"
        }`}
      >
        <BrandBlock compact={collapsed} />
        <NavItems items={items} compact={collapsed} />
        <UserFooter user={user} compact={collapsed} onLogout={handleLogout} />
      </aside>

      {/* ── Mobile drawer (below lg) ────────────────────────────────────── */}
      <AnimatePresence>
        {drawerOpen && (
          <>
            <motion.div
              key="backdrop"
              data-testid="sidebar-backdrop"
              className="fixed inset-0 bg-slate-900/50 backdrop-blur-[2px] z-30 lg:hidden no-print"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setDrawerOpen(false)}
            />
            <motion.aside
              key="drawer"
              data-testid="sidebar-drawer"
              className="fixed inset-y-0 left-0 w-72 max-w-[85vw] bg-white border-r hairline z-40 lg:hidden flex flex-col no-print shadow-2xl"
              initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 280 }}
            >
              <div className="absolute top-3 right-3 z-20">
                <button
                  data-testid="sidebar-drawer-close"
                  onClick={() => setDrawerOpen(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-sm border hairline hover:border-myb-500 hover:text-myb-500 text-muted-foreground bg-white"
                  aria-label="Close menu"
                ><X size={14} /></button>
              </div>
              <BrandBlock compact={false} />
              <NavItems items={items} compact={false} onNavigate={() => setDrawerOpen(false)} />
              <UserFooter user={user} compact={false} onLogout={handleLogout} />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* ── Main ─────────────────────────────────────────────────────────── */}
      <main className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-10 bg-white/95 backdrop-blur-md border-b hairline px-4 sm:px-6 lg:px-10 py-3 sm:py-4 flex items-center justify-between gap-3 no-print">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            {/* Mobile hamburger */}
            <button
              data-testid="sidebar-mobile-toggle"
              onClick={() => setDrawerOpen(true)}
              aria-label="Open menu"
              className="lg:hidden w-9 h-9 flex items-center justify-center rounded-sm border hairline text-myb-500 hover:bg-myb-50 shrink-0"
            ><ListIcon weight="bold" size={18} /></button>

            {/* Desktop collapse toggle */}
            <button
              data-testid="sidebar-collapse-toggle"
              onClick={() => setCollapsed((c) => !c)}
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              className="hidden lg:inline-flex w-9 h-9 items-center justify-center rounded-sm border hairline text-myb-500 hover:bg-myb-50 shrink-0"
            >
              {collapsed ? <CaretDoubleRight weight="bold" size={14} /> : <CaretDoubleLeft weight="bold" size={14} />}
            </button>

            {/* Mobile brand mini-lockup */}
            <div className="lg:hidden flex items-center gap-2 min-w-0">
              <div className="w-8 h-8 rounded-sm bg-myb-500 flex items-center justify-center shrink-0">
                <img src="/myb-logo.png" alt="" className="w-6 h-6 object-contain" style={{ filter: "brightness(0) invert(1)" }} />
              </div>
              <div className="min-w-0">
                <div className="font-display text-sm text-foreground font-semibold truncate">Behbehani</div>
                <div className="text-[9px] text-myb-500 uppercase tracking-[0.25em]">Since 1935</div>
              </div>
            </div>

            <div className="hidden lg:block min-w-0">
              <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground font-semibold">Executive Workspace</div>
              <div className="text-sm text-foreground/80 truncate">Welcome back, {user?.name?.split(" ")[0]}</div>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            {dataInfo?.latest_data_date && (() => {
              const latest = new Date(dataInfo.latest_data_date);
              const today = new Date();
              const daysSince = Math.max(0, Math.floor((today - latest) / 86400000));
              const tone =
                daysSince > 30 ? "rose"
                : daysSince > 7 ? "amber"
                : "emerald";
              const toneClasses = {
                emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
                amber: "bg-amber-50 border-amber-200 text-amber-700",
                rose: "bg-rose-50 border-rose-200 text-rose-700",
              }[tone];
              const toneLabel =
                daysSince === 0 ? "today"
                : daysSince === 1 ? "1 day ago"
                : `${daysSince} days ago`;
              return (
                <div
                  data-testid="header-data-as-of"
                  data-stale-tone={tone}
                  title={`All KPIs, presets (Last 7d / MTD / QTD / YTD), target proration & chatbot answers anchor on this date.\nLatest sale: ${dataInfo.latest_data_date} (${toneLabel})${dataInfo.last_upload?.uploaded_at ? `\nLast upload: ${new Date(dataInfo.last_upload.uploaded_at).toLocaleString()} by ${dataInfo.last_upload.uploaded_by || ""}` : ""}${tone === "amber" ? "\n⚠ Data is over a week old — consider uploading a fresh file." : ""}${tone === "rose" ? "\n⚠ Data is over a month old — please upload the latest file." : ""}`}
                  className="inline-flex items-center gap-2 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-full bg-myb-50 border border-myb-200 hover:border-myb-500 transition-all"
                >
                  <Database weight="duotone" size={14} className="text-myb-500 shrink-0" />
                  <div className="leading-tight">
                    <div className="text-[8px] sm:text-[9px] uppercase tracking-[0.2em] text-myb-500 font-semibold">Data as of</div>
                    <div className="text-[11px] sm:text-xs font-semibold text-foreground tabular-nums whitespace-nowrap">
                      {new Date(dataInfo.latest_data_date).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" })}
                    </div>
                  </div>
                  <span
                    data-testid="header-data-stale-chip"
                    className={`hidden sm:inline-block text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full border whitespace-nowrap ${toneClasses}`}
                  >
                    {toneLabel}
                  </span>
                </div>
              );
            })()}

            <NavLink
              to="/chat"
              data-testid="chatbot-open-btn"
              className="group inline-flex items-center gap-2 pl-2.5 pr-3 sm:pl-3 sm:pr-4 py-1.5 sm:py-2 rounded-full bg-myb-500 text-white hover:bg-myb-600 transition-all duration-300 shadow-sm"
            >
              <Sparkle weight="fill" size={14} className="group-hover:rotate-12 transition-transform" />
              <span className="text-[11px] sm:text-xs tracking-wide font-medium whitespace-nowrap">
                <span className="hidden sm:inline">Ask the </span>Analyst
              </span>
            </NavLink>
          </div>
        </header>

        <div className="flex-1 relative">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="p-4 sm:p-6 lg:p-10 relative"
          >
            {children}
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default Layout;
